// EasyPayDetailDialog.tsx
import React from 'react';

import CheckmarkSmallSvg from '@/components/common/CheckmarkSmallSvg';
import LoadingSvg from '@/components/common/LoadingSvg';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

import { EasyPayScheduleItem, usePaymentHistoryDetail } from '../hooks/useEasyPaySchedule';

import { CardExpiryWarning } from './CardExpiryWarning';

type EasyPayDetailDialogProps = {
    order: EasyPayScheduleItem | null;
};

export const EasyPayDetailDialog: React.FC<EasyPayDetailDialogProps> = ({ order }) => {
    const { data: detailData, isLoading, error } = usePaymentHistoryDetail(order?.FullOrderNo);

    // Early return if no order
    if (!order) {
        return (
            <div className="flex min-h-96 items-center justify-center">
                <div className="text-center text-gray-500">No order selected</div>
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="" id="dialog-easypay-detail">
                <DialogHeader>
                    <DialogTitle className="mt-1.5 text-center font-bold">Payment History</DialogTitle>
                    <DialogDescription className="text-center font-light">
                        Order Number: {order.FullOrderNo}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex min-h-96 items-center justify-center bg-white">
                    <div className="flex flex-col items-center">
                        <LoadingSvg />
                        Loading Payment History
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="" id="dialog-easypay-detail">
                <DialogHeader>
                    <DialogTitle className="mt-1.5 text-center font-bold">Payment History</DialogTitle>
                    <DialogDescription className="text-center font-light">
                        Order Number: {order.FullOrderNo}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex min-h-96 items-center justify-center">
                    <div className="text-center text-red-600">Error loading payment history</div>
                </div>
            </div>
        );
    }

    // No data or empty array
    if (!detailData || detailData.length === 0) {
        return (
            <div className="" id="dialog-easypay-detail">
                <DialogHeader>
                    <DialogTitle className="mt-1.5 text-center font-bold">Payment History</DialogTitle>
                    <DialogDescription className="text-center font-light">
                        Order Number: {order.FullOrderNo}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex min-h-96 items-center justify-center">
                    <div className="text-center text-gray-500">No payment history available</div>
                </div>
            </div>
        );
    }

    // Find first non-billed index
    const firstNonBilledIndex = detailData.findIndex((detail) => detail.Status !== 'Billed');
    // Calculate total amount due
    const total = detailData.reduce((sum, detail) => {
        return sum + parseFloat(detail.AmountDue);
    }, 0);
    const formattedTotal = `$${total.toFixed(2)}`;

    return (
        <div className="" id="dialog-easypay-detail">
            <DialogHeader>
                <DialogTitle className="mt-1.5 text-center font-bold">Payment History</DialogTitle>
                <DialogDescription className="text-center font-light">
                    Order Number: {order.FullOrderNo}
                </DialogDescription>
            </DialogHeader>
            <div className="mt-4 w-full">
                <div className="grid grid-cols-[1fr_1fr_1fr] text-sm font-light leading-6">
                    <div
                        className="flex justify-center py-2 pl-1 text-center text-sm font-bold uppercase leading-5 text-black"
                        style={{ borderBottom: '1px solid #000' }}
                    >
                        <div className="w-24 text-left">Payment</div>
                    </div>
                    <div
                        className="flex justify-center py-2 pr-1 text-center text-sm font-bold uppercase leading-5 text-black"
                        style={{ borderBottom: '1px solid #000' }}
                    >
                        <div className="w-24 text-center">Bill Date</div>
                    </div>
                    <div
                        className="flex justify-center py-2 pr-1 text-center text-sm font-bold uppercase leading-5 text-black"
                        style={{ borderBottom: '1px solid #000' }}
                    >
                        <div className="w-24 text-right">Amount</div>
                    </div>
                    {detailData.map((detail, index) => {
                        const isFirstNonBilled = index === firstNonBilledIndex;
                        const isBilled = detail.Status === 'Billed';
                        return (
                            <React.Fragment key={index + detail.BillDate}>
                                <div className="flex justify-center gap-1 pt-4">
                                    <div className="mr-7 flex w-12 justify-evenly">
                                        {isFirstNonBilled && (
                                            <>
                                                {order.CardExpiresInDays === -1 ? (
                                                    <div className="h-5 w-5"> </div>
                                                ) : (
                                                    <CardExpiryWarning
                                                        expiresInDays={order.CardExpiresInDays}
                                                        collisionBoundary="dialog-easypay-detail"
                                                        portalID="easypay-popover-portal"
                                                    />
                                                )}
                                            </>
                                        )}
                                        {isBilled && (
                                            <div className="h-5 w-5">
                                                <CheckmarkSmallSvg />
                                            </div>
                                        )}
                                        {!isBilled && !isFirstNonBilled && <div className="h-5 w-5"></div>}
                                        <div>{index + 1}</div>
                                    </div>
                                </div>
                                <div className="flex justify-center pt-4 text-center">
                                    <div className="w-23 text-right">{detail.BillDate}</div>
                                </div>
                                <div className="flex justify-center pt-4">
                                    <div className="w-20 text-right">{detail.Amount}</div>
                                </div>
                            </React.Fragment>
                        );
                    })}
                    <div
                        className="mt-4 flex justify-center py-2 pl-1 text-center text-sm font-bold uppercase leading-5 text-black"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    >
                        <div className="w-24 whitespace-nowrap text-left">Remaining Total:</div>
                    </div>
                    <div
                        className="mt-4 flex justify-center py-2 pr-1 text-center text-sm font-bold uppercase leading-5 text-black"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    >
                        <div className="w-24 text-center"></div>
                    </div>
                    <div
                        className="mt-4 flex justify-center py-2 pr-1 text-center text-sm font-bold uppercase leading-5 text-black"
                        style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000' }}
                    >
                        <div className="w-24 text-right">{formattedTotal}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
